import { DOCUMENT } from '@angular/common'
import { Directive, ElementRef, Inject, Input, OnDestroy, OnInit } from '@angular/core'
import { adjustLightness } from '../utilities/rgb-to.hsl'

type VenueColourVariables = {
    '--app-primary-colour': string
    '--app-primary-colour-light': string
    '--app-primary-colour-dark': string
}

@Directive({
    selector: '[appVenueColour]',
    standalone: true,
})
export class VenueColourDirective implements OnInit, OnDestroy {

    private static colourVariableNames: Array<keyof VenueColourVariables> = [
        '--app-primary-colour',
        '--app-primary-colour-light',
        '--app-primary-colour-dark',
    ]

    private static documentColourSources: Array<{
        directive: VenueColourDirective
        variables: VenueColourVariables
    }> = []

    private primaryColourValue?: string | null
    private primaryColourInputBound = false

    @Input()
    set primaryColour(value: string | null | undefined) {
        this.primaryColourValue = value
        this.primaryColourInputBound = true
        this.updateColourVariable()
    }

    constructor(
        private elementRef: ElementRef<HTMLElement>,
        @Inject(DOCUMENT) private document: Document
    ) { }

    ngOnInit() {
        this.updateColourVariable()
    }

    ngOnDestroy() {
        VenueColourDirective.documentColourSources = VenueColourDirective.documentColourSources
            .filter(source => source.directive !== this)
        this.applyLatestDocumentColour()
    }

    private updateColourVariable() {
        let variables = this.getColourVariables()
        this.applyColourVariables(this.elementRef.nativeElement, variables)
        if (this.primaryColourInputBound) {
            this.updateDocumentColourSource(variables)
        }
    }

    private getColourVariables(): VenueColourVariables {
        let primaryColour = this.primaryColourValue ?? '21, 136, 73'
        let lightColour = adjustLightness(primaryColour, 0.2)
        let darkColour = adjustLightness(primaryColour, -0.2)
        return {
            '--app-primary-colour': this.addRGBSelector(primaryColour),
            '--app-primary-colour-light': this.addRGBSelector(lightColour),
            '--app-primary-colour-dark': this.addRGBSelector(darkColour),
        }
    }

    private updateDocumentColourSource(variables: VenueColourVariables) {
        let source = VenueColourDirective.documentColourSources
            .find(source => source.directive === this)
        if (source) {
            source.variables = variables
        } else {
            VenueColourDirective.documentColourSources.push({
                directive: this,
                variables,
            })
        }
        this.applyLatestDocumentColour()
    }

    private applyLatestDocumentColour() {
        let latestSource = VenueColourDirective.documentColourSources[
            VenueColourDirective.documentColourSources.length - 1
        ]
        if (latestSource) {
            this.applyColourVariables(this.document.body, latestSource.variables)
        } else {
            this.removeColourVariables(this.document.body)
        }
    }

    private applyColourVariables(element: HTMLElement, variables: VenueColourVariables) {
        Object.entries(variables)
            .forEach(([property, value]) => element.style.setProperty(property, value))
    }

    private removeColourVariables(element: HTMLElement) {
        VenueColourDirective.colourVariableNames
            .forEach(property => element.style.removeProperty(property))
    }

    private addRGBSelector(rgb: string): string {
        return `rgb(${rgb})`
    }
}
